const WEB_SIGNUP_PLATFORM = "web";

const platformContext = () => ({ signup_platform: WEB_SIGNUP_PLATFORM });

const getCurrentPosition = (options) =>
  new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });

const finiteCoordinate = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getIpLocation = async () => {
  if (typeof fetch !== "function") return null;

  const sources = [
    {
      url: "https://ipapi.co/json/",
      read: (data) => ({ latitude: data?.latitude, longitude: data?.longitude }),
    },
    {
      url: "https://ipwho.is/",
      read: (data) => data?.success === false
        ? null
        : { latitude: data?.latitude, longitude: data?.longitude },
    },
  ];

  for (const source of sources) {
    try {
      const response = await fetch(source.url);
      if (!response.ok) continue;
      const raw = source.read(await response.json());
      const latitude = finiteCoordinate(raw?.latitude);
      const longitude = finiteCoordinate(raw?.longitude);
      if (latitude !== null && longitude !== null) return { latitude, longitude };
    } catch (error) {
      // Try the next provider. Signup must still work if both are unavailable.
    }
  }
  return null;
};

export const getWebSignupLocationContext = async () => {
  const context = platformContext();

  try {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      throw new Error("Browser geolocation is unavailable");
    }
    let position;
    try {
      position = await getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      });
    } catch (error) {
      position = await getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 7000,
        maximumAge: 600000,
      });
    }

    const { latitude, longitude, accuracy } = position?.coords || {};
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      throw new Error("Browser returned invalid coordinates");
    }

    return {
      ...context,
      signup_latitude: latitude,
      signup_longitude: longitude,
      ...(Number.isFinite(accuracy)
        ? { signup_location_accuracy: accuracy }
        : {}),
    };
  } catch (error) {
    // Browsers can block GPS because of permission, timeout, or a non-secure
    // context. Use coarse IP coordinates so web signups still have a useful
    // map location, while keeping signup available if every provider fails.
    const approximate = await getIpLocation();
    return approximate
      ? {
          ...context,
          signup_latitude: approximate.latitude,
          signup_longitude: approximate.longitude,
        }
      : context;
  }
};

export { WEB_SIGNUP_PLATFORM };
